/** Brukergrensesnitt: sidepanel med sammenleggbare seksjoner, kartkontroller, overlegg og hendelser. */

import type { ProjectInfo } from './api';
import type { ViewerConfig } from './config';
import { CRS_NAMES, el, formatBytes, formatCoord, formatCount, naturalCompare } from './format';
import { KIND_TITLES } from './layers/catalog';
import type { ImageLoader } from './layers/images';
import type { DroneImageController } from './images/controller';
import type { OverlayManager, Picked, VectorLayer } from './layers/overlays';
import type { BaseMap } from './map/basemap';
import type { BaseMapMode } from './map/tiles';
import { classInfo } from './scene/classes';
import { crsAssessment } from './scene/coordinates';
import type { Engine } from './scene/engine';
import type { PointcloudLayer } from './scene/layer';
import type { ColorMode } from './scene/material';
import { renderSelection, type Selection } from './ui/objectInfo';
import { section, sectionDefaults } from './ui/section';
import { objectRows, filterRows } from './ui/objectRows';
import { transformer } from './geo/crs';
import { Workbench } from './ui/workbench';
import { ReviewController } from './review/controller';
import { ReviewStore } from './gis/review';
import { saveBlob } from './gis/downloads';
import { placeSearch } from './ui/placeSearch';
import {icon} from './ui/icons';
import {panelResizer} from './ui/resize';
import {gallery} from './ui/gallery';
import {projectImages} from './layers/imageCatalog';
import {cardRows, fallContactIndex, resultCard, resultPanel, type FindingContext} from './ui/resultPanel';
import {shortcutFor} from './ui/shortcuts';
import {applyTheme, currentTheme, readThemePref, toggleTheme} from './ui/theme';
import {hintCard, hintSeen, markHintSeen} from './ui/firstRun';
import {densityText, projectOptions, runMeta, shortProjectName} from './ui/projectMeta';
import {DENSITY_GRADIENT} from './scene/density';
import {parseLeveranseprofil, setLeveranseView, type Leveranseprofil} from './layers/leveranse';
import {clearVisibleLayers, visibleLayerIds, writeFallFilter, writeVisibleLayers} from './layers/layerPrefs';
import {FALL_EMPTY_TEXT, FALL_HELP, FALL_NO_FIELD_NOTE, FALL_LAYER, FALL_PART_LAYERS, FALL_S_MIN, fallCounts, fallCountText, fallFilter, fallMargin, fallMarginHint, setFallFilter} from './layers/fallRisk';
import {CLEARANCE_LEGEND, CLEARANCE_TOGGLE, FALL_LEGEND, FALL_TOGGLE, hiddenFromLayerList, setHideUnwrittenSupports, SUPPORT_CANDIDATE_LAYERS, UNCERTAIN_SUPPORTS_TOGGLE, VEG_NOTE, WRITE_STYLE_LAYERS, WRITE_STYLE_NOTE, ALL_GROUND_WIRES_TOGGLE, GROUND_LAYERS, setShowAllGroundWires, viewOptions} from './layers/featureStyle';

const STATUS_TEXT: Record<string, string> = {
  OK: 'OK', WARNING_QC: 'Advarsel QC', FAILED_QC: 'QC feilet', REVIEW: 'Til kontroll',
  REFLY_RECOMMENDED: 'Refly anbefalt', REFLY_REQUIRED: 'Refly påkrevd', UNKNOWN: 'Ukjent',
};

const MODES: { id: ColorMode; label: string }[] = [
  { id: 'rgb', label: 'RGB' }, { id: 'intensity', label: 'Intensitet' },
  { id: 'classification', label: 'Klasse' }, { id: 'elevation', label: 'Høyde' }, { id: 'density', label: 'Tetthet' },
];
const BASEMAPS: { id: BaseMapMode; label: string }[] = [{ id: 'map', label: 'Norgeskart' }, { id: 'none', label: 'Ingen' }];

const VSTATUS: Record<string, string> = {
  absent: 'Ikke i prosjektet ennå', idle: 'Ikke lastet', loading: 'Laster …', ready: '', empty: 'Ingen objekter',
  unavailable: 'Mangler kilde', error: 'Uleselig kilde', access_error:'Mangler tilgang', network_error:'Nettverksfeil',
};

export interface AppParts {
  engine: Engine;
  project: ProjectInfo;
  cfg: ViewerConfig;
  basemap: BaseMap;
  overlays: OverlayManager;
  images: ImageLoader;
  /** dronebilder (kameramarkører, lysboks, «Se bilder av denne masten») */
  droneImages?: DroneImageController;
}

export class ViewerUI {
  private rows = new Map<string, { row: HTMLElement; box: HTMLInputElement; status: HTMLElement; meta: HTMLElement }>();
  private cloudList:HTMLElement|null=null;
  private vrows = new Map<string, { row: HTMLElement; box: HTMLInputElement; status: HTMLElement; detail: HTMLElement; filter:HTMLInputElement; retry:HTMLButtonElement }>();
  private statsEl!: HTMLElement;
  private coordEl!: HTMLElement;
  private legendEl!: HTMLElement;
  private attrEl!: HTMLElement;
  private crsCheckEl!: HTMLElement;
  private techEl!: HTMLElement;
  private infoBody!: HTMLElement;
  private infoSection!: ReturnType<typeof section>;
  private cameraInfo!: HTMLElement;
  private modeButtons = new Map<ColorMode, HTMLButtonElement>();
  private mapButtons = new Map<BaseMapMode, HTMLButtonElement>();
  private viewButtons = new Map<'3d' | '2d', HTMLButtonElement>();
  private countEl!: HTMLElement;
  private filter = '';
  private hiddenClasses = new Set<number>();
  private classPreset = 'all';
  private raf = 0;
  private legendKey='';
  private techVisible=false;
  private crsKey='';
  private selection: Selection | null = null;
  private inspector!: HTMLElement;
  private shell!: HTMLElement;
  private menuButton!: HTMLButtonElement;
  private backdrop!: HTMLElement;
  private searchInput!: HTMLInputElement;
  private filterMessage!: HTMLElement;
  private tablePanel!: HTMLElement;
  private tableBody!: HTMLElement;
  private tableCount!: HTMLElement;
  private tableSearch = '';
  private tableSort: 'name' | 'layer' | 'status' | 'review' = 'name';
  private tablePage = 0;
  private tableKey = '';
  private workbench!:Workbench;
  private reviews!:ReviewStore;
  private vectorList!:HTMLElement;
  private qcList!:HTMLElement;
  private imageList!:HTMLElement;
  private layerSummary!:HTMLElement;
  private inspectorTitle!:HTMLElement;
  private imageBody!:HTMLElement;
  private pendingNationalTarget: [number,number] | null = null;
  readonly el: { side?: HTMLElement; stage?: HTMLElement } = {};
  /** «Resultat»-visning for lokale classify-prosjekter (null for andre prosjekter) */
  private result: ReturnType<typeof resultPanel> | null = null;
  /** Resultat-visning: vis bæreobjekt-kandidater som ikke er skrevet til punktskyen (av som standard) */
  private showUncertainSupports = false;
  /** kundens leveranseprofil (prosjekt-JSON `leveranseprofil`) */
  private profil: Leveranseprofil = parseLeveranseprofil(undefined);
  /** «Vis usikre mastkandidater» i Full visning (samme tilstand som bryteren i Funn) */
  private uncertainBox: HTMLInputElement | null = null;
  /** Funn-steppens plass for valgt objekt (null når objektet er valgt på annen måte) */
  private findingCtx: FindingContext | null = null;
  private inspectorSub!: HTMLElement;
  private findingBar!: HTMLElement;
  private sideTitle!: HTMLElement;
  private hintEl: HTMLElement | null = null;
  private modeSeg = new Map<'result' | 'full', HTMLButtonElement>();
  private themeButton!: HTMLButtonElement;
  /** Samlebrytere (fallrisiko, minsteavstand) i Full visning; synkes i refresh() */
  private riskBoxes: { box: HTMLInputElement; layers: VectorLayer[] }[] = [];
  /** sist lagrede lagvalg (JSON) – lagres per prosjekt når synligheten endres */
  private layerPrefKey = '';
  /** kontrollmodus (K): rask gjennomgang med kontrollstripe; korrigeringer → motoren */
  review: ReviewController | null = null;
  private reviewButton: HTMLElement | null = null;

  constructor(private root: HTMLElement, private app: AppParts) {}

  private get engine() {
    return this.app.engine;
  }

  build(canvas: HTMLCanvasElement): void {
    const mobile = matchMedia('(max-width: 900px)').matches;
    if (mobile) for (const id of ['project', 'crs', 'clouds', 'images', 'tech']) sectionDefaults[id] = false;
    const { project: p, cfg } = this.app;
    const status = p.status ?? 'UNKNOWN';
    let storage:Storage|undefined;try{storage=localStorage;}catch{}
    this.reviews=new ReviewStore(p.projectId,storage);
    this.workbench=new Workbench(this.engine,this.app.overlays,canvas,p.horizontalCrs??'UNKNOWN',p.verticalCrs??'UNKNOWN');
    if(this.workbench.classification.hasSavedState)this.workbench.restoreSavedClassification();
    this.workbench.onImport=()=>{this.renderLayerGroups();this.schedule();};
    const classify=(p.raw.classify??null) as {name?:unknown}|null;
    this.workbench.exportContext=()=>({resultMode:!!this.result&&!!this.shell?.classList.contains('result-mode'),includeUncertain:this.showUncertainSupports,
      project:typeof p.raw.project_name==='string'&&p.raw.project_name?p.raw.project_name:p.projectId,run:typeof classify?.name==='string'?classify.name:null});
    const list = el('div', { class: 'pc-list' });
    this.cloudList=list;
    const layers = [...this.engine.layers].sort((a, b) => naturalCompare(a.entry.name, b.entry.name));
    for (const l of layers) list.append(this.layerRow(l));
    for (const bad of p.invalid) {
      list.append(el('div', { class: 'pc-row pc-invalid' }, el('span', { class: 'pc-name' }, `Oppføring #${bad.index + 1}`),
        el('span', { class: 'pc-status error' }, `Ugyldig i API: ${bad.reason}`)));
    }
    this.countEl = el('span', { class: 'muted' });
    this.crsCheckEl = el('div', { class: 'crs-check muted' }, 'Kontrolleres mot filheadere …');
    this.techEl = el('div', { class: 'tech' });
    const totalSize = p.pointclouds.reduce((s, c) => s + (c.size ?? 0), 0);
    const declared = this.app.overlays.layers.filter((l) => l.def.inProject);
    // fallrisiko vises som én bryter (riskGroup); eldre fall-/faretre-lag vises ikke i laglistene
    const listed = declared.filter((l) => !hiddenFromLayerList(l.def.id));
    const vectorList = this.vectorList = el('div', { class: 'v-list' }, ...listed.filter((l) => !['cameras','qc','refly'].includes(l.def.kind)).map((l) => this.vectorRow(l)));
    const cams = declared.filter((l) => l.def.kind === 'cameras');
    this.qcList=el('div',{class:'v-list'},...listed.filter(l=>['qc','refly'].includes(l.def.kind)).map(l=>this.vectorRow(l)));
    this.imageList=el('div',{class:'v-list'},...cams.map(l=>this.vectorRow(l)));
    this.layerSummary=el('div',{class:'layer-summary','aria-live':'polite'});
    this.cameraInfo = el('div', { class: 'note' });
    this.infoBody = el('div', {}, renderSelection(null, this.infoContext()));

    const sProject = section('project', 'Prosjektdetaljer', el('div', {},
      el('h1', {}, p.projectName),
      el('div', { class: 'kv' },
        el('span', {}, 'Prosjekt-ID'), el('b', {}, p.projectId || cfg.projectId),
        p.customerId ? el('span', {}, 'Kunde') : null, p.customerId ? el('b', {}, p.customerId) : null,
        el('span', {}, 'QC-status'), el('b', {}, el('span', { class: `chip st-${status}` }, STATUS_TEXT[status] ?? status)),
        el('span', {}, 'Punktskyer'), el('b', {}, `${p.pointclouds.length} · ${formatBytes(totalSize)}`)),el('div',{class:'note'},status==='WARNING_QC'?'Prosjektet har QC-advarsel fra API. Se QC-gruppen; årsaken kan bare spesifiseres når funn er tilgjengelige.':'QC-status er fra prosjekt-API.'),el('button',{onclick:()=>{this.toggleSide(true);sQc.setOpen(true);sQc.root.scrollIntoView({block:'nearest'});}},'Vis QC-gruppen')), {open:false});
    const sCrs = section('crs', 'Koordinatsystem', el('div', {},
      el('div', { class: 'kv' },
        el('span', {}, 'Horisontalt'), el('b', {}, `${p.horizontalCrs ?? 'ukjent'}`, el('small', {}, CRS_NAMES[p.horizontalCrs ?? ''] ?? '')),
        el('span', {}, 'Vertikalt'), el('b', {}, `${p.verticalCrs ?? 'ukjent'}`, el('small', {}, CRS_NAMES[p.verticalCrs ?? ''] ?? ''))),
      this.crsCheckEl), {open:false,remember:false});
    const sView = section('background', 'Bakgrunn og terreng', el('div', {},
      this.seg('Bakgrunn', BASEMAPS, this.mapButtons, (m) => this.setBasemap(m)),
      el('label', {class:'slider'}, 'Nasjonalt 3D-terreng', el('input', {type:'checkbox',checked:true,onchange:(ev:Event)=>this.app.basemap.setTerrain((ev.target as HTMLInputElement).checked)})),
      placeSearch(place=>{const [x,y]=transformer('EPSG:4258',this.app.project.horizontalCrs!)(place.east,place.north);this.pendingNationalTarget=[x,y];this.engine.zoomTo({min:[x-2000,y-2000,0],max:[x+2000,y+2000,1500]});},this.app.basemap.available),
      el('details',{},el('summary',{},'Kilde og høydereferanse'),el('div',{class:'note'},'Kartverket landterreng. Hav, innsjøer og kildens hull bruker flat reserve, ikke målt terreng.'))), {open:false,remember:false});
    const pointStyle=section('point-style','Punktskyvisning',el('div',{},
      this.seg('Farge', MODES, this.modeButtons, (m)=>this.setMode(m)),
      el('label',{class:'slider'},'Punktstørrelse',el('input',{type:'range',min:'1',max:'8',step:'0.5',value:'2',oninput:(e:Event)=>this.engine.setPointSize(Number((e.target as HTMLInputElement).value))})),
      el('label',{class:'slider'},'Punktbudsjett',el('select',{onchange:(e:Event)=>this.engine.setPointBudget(Number((e.target as HTMLSelectElement).value))},...[1e6,1.5e6,2e6,4e6,8e6,15e6].map(v=>el('option',{value:String(v),selected:v===cfg.pointBudget},`${v/1e6} mill. punkter`))))),{open:false});
    this.filterMessage = el('div', { class: 'note', 'aria-live': 'polite' });
    this.searchInput = el('input', { class: 'search', type: 'search', placeholder: 'Søk i punktskyer …', 'aria-label': 'Søk i punktskylisten', oninput: (e: Event) => { this.filter = (e.target as HTMLInputElement).value.toLowerCase(); this.applyFilter(); } });
    const sClouds = section('clouds', 'Punktskyer', el('div', { class: 'grow-col' },
      el('div', { class: 'row-head' }, this.countEl),
      el('div', { class: 'toolbar' },
        el('button', { onclick: () => this.setAll(true) }, 'Vis alle'),
        el('button', { onclick: () => this.setAll(false) }, 'Skjul alle'),
        el('button', { class: 'primary', onclick: () => this.engine.zoomTo(this.engine.projectBounds()) }, 'Zoom til prosjekt')),
      this.searchInput, el('button', { class: 'link', onclick: () => { this.searchInput.value = ''; this.filter = ''; this.applyFilter(); } }, 'Nullstill søk'), this.filterMessage,
      list,pointStyle.root), {grow:true,open:true,remember:false});
    const sVectors = section('vectors', 'Vektorlag', el('div', {},
      el('div', { class: 'res-class-tools' }, el('button', { type: 'button', class: 'btn btn-sm', onclick: () => this.resetDefaultView() }, icon('reset'), 'Tilbakestill visning')),
      this.riskGroup(declared),
      ...(declared.some((l) => (SUPPORT_CANDIDATE_LAYERS as readonly string[]).includes(l.def.id)) ? [el('label', { class: 'res-sub uncertain-toggle' },
        this.uncertainBox = el('input', { type: 'checkbox', checked: this.showUncertainSupports, 'aria-label': UNCERTAIN_SUPPORTS_TOGGLE,
          onchange: () => { this.showUncertainSupports = this.uncertainBox!.checked; this.applySupportVisibility(); } }) as HTMLInputElement,
        el('span', {}, UNCERTAIN_SUPPORTS_TOGGLE))] : []),
      // bakkeavstand: Resultat viser én måling per spenn; alle målinger bare i Full visning
      ...(declared.some((l) => (GROUND_LAYERS as readonly string[]).includes(l.def.id)) ? [el('label', { class: 'res-sub uncertain-toggle' },
        el('input', { type: 'checkbox', checked: viewOptions.showAllGroundWires, 'aria-label': ALL_GROUND_WIRES_TOGGLE,
          onchange: (e: Event) => { setShowAllGroundWires((e.target as HTMLInputElement).checked); for (const id of GROUND_LAYERS) this.app.overlays.rebuild(id); this.result?.reset(); this.schedule(); } }),
        el('span', {}, ALL_GROUND_WIRES_TOGGLE))] : []),
      vectorList,
      ...(declared.some((l) => WRITE_STYLE_LAYERS.has(l.def.id)) ? [el('div', { class: 'note' }, WRITE_STYLE_NOTE)] : []),
      el('button',{class:'small-btn',onclick:()=>this.workbench.show('layers')},'Importer GeoPackage / GeoJSON'),
      el('div', { class: 'note' }, 'Lag hentes via Worker-API-et først når de slås på. Manglende lag vises med status.')), {open:false,remember:false});
    const sQc=section('qc','Analyse-lag',el('div',{},this.qcList,el('div',{class:'note'},'QC/fagstatus fra analyse er separat fra lokal review. Manglende funn betyr ikke bestått QC.')), {open:false,remember:false});
    const sImages=section('images','Dronebilder',el('div',{},this.app.droneImages?.panelRow()??null,this.imageList,this.cameraInfo,el('button',{class:'small-btn',disabled:!projectImages(p.raw.images).length,onclick:()=>this.openImages()},'Åpne bildegalleri')), {open:false,remember:false});
    this.infoSection=section('info','Objekt / kilde',this.infoBody);
    const sTech = section('tech', 'Teknisk info', this.techEl, {open:false,remember:false});
    sTech.root.querySelector('button')!.addEventListener('click',()=>{this.techVisible=sTech.open;if(this.techVisible)this.updateTech();});

    const local = p.raw.source === 'local-api';
    this.profil = parseLeveranseprofil(p.raw.leveranseprofil);
    if (local) this.result = resultPanel({
      raw: p.raw,
      classCounts: () => { const m = new Map<number, number>(); for (const l of this.engine.layers) for (const [c, n] of l.classCounts) m.set(c, (m.get(c) ?? 0) + n); return m; },
      hiddenClasses: () => new Set(this.engine.style.hiddenClasses),
      setHiddenClasses: (s) => { this.hiddenClasses = new Set(s); this.classPreset = 'custom'; this.engine.setHiddenClasses(this.hiddenClasses); this.engine.requestRender(); this.renderLegend(); this.schedule(); },
      layers: () => this.app.overlays.layers,
      setVisible: (id, v) => this.app.overlays.setVisible(id, v),
      load: (l) => void this.app.overlays.load(l),
      goTo: (picked, ctx) => this.goToFeature(picked, ctx),
      showFull: () => this.setResultMode(false),
      showUncertainSupports: () => this.showUncertainSupports,
      setShowUncertainSupports: (v) => { this.showUncertainSupports = v; this.applySupportVisibility(); },
      resetView: () => this.resetDefaultView(),
      extraGroup: () => this.app.droneImages?.funnGroupEl() ?? null,
      profil: this.profil,
      onAudience: () => this.applyLeveranseView(),
    });
    // kundevisningen styrer tegningen fra start (Resultat er standard for lokale prosjekter)
    setLeveranseView(!!this.result?.customer(), this.profil);
    // Usikre mastkandidater (ikke skrevet til punktskyen) er skjult som standard i begge visninger (før lagene bygges)
    setHideUnwrittenSupports(!this.showUncertainSupports);
    const side = el('aside', { class: 'side', 'aria-label': 'Funn, klasser og lag' },
      el('header', { class: 'panel-head' },
        this.sideTitle = el('h2', { class: 'panel-title' }, local ? 'Resultat' : 'Lag og data'),
        tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label': 'Lukk venstre panel', onclick: () => this.toggleSide(false) }, icon('close')), 'Lukk panel')),
      el('div',{class:'side-scroll'},this.result?.root ?? null,this.layerSummary,sClouds.root,sVectors.root,sQc.root,sImages.root,sView.root,sCrs.root,sProject.root,sTech.root));

    // CRS-detaljene ligger i teknisk info. Oppdateringer av tech får egen beholder.
    const techDetails = el('div');
    this.techEl.prepend(techDetails);
    this.techEl = techDetails;
    this.findingBar = el('div', { class: 'finding-bar hidden', role: 'group', 'aria-label': 'Plass i funngjennomgangen' });
    this.inspector = el('aside', { class: 'inspector hidden', 'aria-label': 'Valgt objekt' },
      el('header',{class:'panel-head'},
        el('div',{class:'panel-head-text'},this.inspectorTitle=el('h2',{class:'panel-title'},'Objektinfo'),this.inspectorSub=el('span',{class:'panel-sub mono'})),
        tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label':'Lukk høyrepanel',onclick:()=>{this.workbench.close();this.closeInfo();}},icon('close')), 'Lukk · Esc')),
      this.findingBar,
      el('div',{class:'inspector-scroll'},this.infoSection.root,this.imageBody=el('div',{class:'project-gallery hidden'})));

    this.statsEl = el('div', { class: 'ov ov-stats' });
    this.coordEl = el('div', { class: 'ov ov-coord mono', title: 'Kameraets senterpunkt i prosjektets koordinatsystem' });
    this.legendEl = el('div', { class: 'ov ov-legend hidden' });
    this.attrEl = el('div', { class: 'ov ov-attr' });
    const toolTip = (b: HTMLElement, text: string) => { b.classList.add('btn-icon'); return tip(b, text, 'right'); };
    const viewSeg = el('div', { class: 'tool-seg', role: 'group', 'aria-label': 'Visning' },
      tip(el('button', { type: 'button', class: 'btn-icon', 'data-view': '2d', 'aria-label': 'Kart (2D ovenfra)', onclick: () => this.setView('2d') }, icon('map')), 'Kart · 2D ovenfra', 'right'),
      tip(el('button', { type: 'button', class: 'btn-icon', 'data-view': '3d', 'aria-label': '3D-visning', onclick: () => this.setView('3d') }, icon('cube')), '3D-visning', 'right'));
    const tools = el('div',{class:'workspace-tools',role:'toolbar','aria-label':'Kartverktøy', 'aria-orientation': 'vertical'},
      toolTip(this.workbench.toolButton('select','Velg objekt','select'), 'Velg objekt (klikk i kartet)'),
      el('div', { class: 'tool-adv' },
        toolTip(this.workbench.toolButton('measure','Linjemåling','measure'), 'Mål avstand mellom to punkter'),
        toolTip(this.workbench.toolButton('profile','Profil (tverrsnitt)','profile'), 'Profil: klikk A og B – sidevisning av korridoren'),
        toolTip(this.workbench.toolButton('rectangle','Rektangelutvalg','rectangle'), 'Rektangelutsnitt (2D)'),
        toolTip(this.workbench.toolButton('polygon','Polygonutsnitt','polygon'), 'Polygonutsnitt (2D)')),
      el('span', { class: 'tool-sep', 'aria-hidden': 'true' }),
      viewSeg,
      el('span', { class: 'tool-sep', 'aria-hidden': 'true' }),
      tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label': 'Zoom til valgt objekt', onclick: () => this.focusSelection() }, icon('target')), 'Zoom til valgt · F', 'right'),
      tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label': 'Vis hele prosjektet', onclick: () => this.resetView() }, icon('reset')), 'Vis hele prosjektet', 'right'),
      tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label': 'Objektinfo', onclick: () => {
        const open = this.inspector.classList.contains('hidden');
        if (open && matchMedia('(max-width: 900px)').matches) this.toggleSide(false);
        if(open)this.openInspector(this.selectionTitle());else this.closeInfo();
      } }, icon('info')), 'Objektinfo', 'right'));
    const help = el('div', { class: 'ov ov-help', 'aria-hidden': 'true' });
    this.tableBody = el('tbody');
    this.tableCount = el('span', { 'aria-live': 'polite', class: 'mono' });
    this.tablePanel = el('section', { class: 'object-table hidden', 'aria-label': 'Objektliste' },
      el('div', { class: 'table-toolbar' }, el('b', {}, 'Objektliste'),
        el('input', { type: 'search', placeholder: 'Søk i lastede objekter …', 'aria-label': 'Søk i objekter', oninput: (e: Event) => { this.tableSearch = (e.target as HTMLInputElement).value; this.tablePage = 0; this.updateTable(); } }),
        el('button', { type: 'button', class: 'btn btn-sm', onclick: () => { this.tableSearch = ''; this.tablePage = 0; this.tablePanel.querySelector('input')!.value = ''; this.updateTable(); } }, 'Nullstill'),
        tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label': 'Lukk objektliste', onclick:()=>{this.tablePanel.classList.add('hidden');this.el.stage?.classList.remove('table-open');} },icon('close')), 'Lukk objektliste', 'left')),
      el('div', { class: 'table-scroll' }, el('table', {}, el('thead', {}, el('tr', {},
        ...(['name', 'layer', 'status', 'review'] as const).map((key, i) => el('th', {}, el('button', { type: 'button', onclick: () => { this.tableSort = key; this.updateTable(); } }, ['Objekt-ID', 'Lag', 'Fag-/QC-status', 'Reviewstatus'][i]))), el('th', {}, 'Handling'))), this.tableBody)),
      el('div', { class: 'table-toolbar' }, this.tableCount,
        el('button', { type: 'button', class: 'btn btn-sm', onclick: () => { this.tablePage = Math.max(0, this.tablePage - 1); this.updateTable(); } }, 'Forrige'),
        el('button', { type: 'button', class: 'btn btn-sm', onclick: () => { this.tablePage++; this.updateTable(); } }, 'Neste')));
    const main=el('main',{class:'stage'},canvas,this.legendEl,help,this.tablePanel);
    const center=el('div',{class:'workspace-center'},tools,main,el('footer',{class:'statusbar'},this.statsEl,this.coordEl,this.attrEl));
    this.workbench.attach(main,this.inspector.querySelector<HTMLElement>('.inspector-scroll')!);
    this.review = new ReviewController({ engine: this.engine, overlays: this.app.overlays, project: p, cfg, stage: main,
      classEdits: () => this.workbench.classification.edits, onChange: () => this.reviewButton?.classList.toggle('active', !!this.review?.isActive) });
    this.reviewButton = this.review.topButton();
    this.workbench.onOpen=mode=>{this.markWorkspaceMode(mode);this.findingBar.classList.add('hidden');this.inspectorSub.textContent='';this.infoSection.root.classList.add('hidden');this.imageBody.classList.add('hidden');this.openInspector(({measure:'Målinger',crop:'Utsnitt',download:'Last ned',layers:'Vektorimport',classify:'Klassifisering / QC'})[mode]);};
    this.workbench.onClose=()=>{this.markWorkspaceMode(this.engine.viewMode);this.infoSection.root.classList.remove('hidden');this.imageBody.classList.add('hidden');if(this.selection){this.openInspector(this.selectionTitle());this.inspectorSub.textContent=this.selectionSub();this.renderFindingBar();}else this.closeInfo();};
    this.el.side = side;
    this.el.stage = main;
    this.menuButton = tip(el('button', { type: 'button', class: 'btn-icon side-toggle', 'aria-label': local ? 'Vis/skjul funnpanelet' : 'Vis/skjul lagpanelet', 'aria-expanded': String(!mobile), onclick: () => this.toggleSide() },icon('layers')), local ? 'Funn og klasser' : 'Lag og data') as HTMLButtonElement;
    const meta = runMeta(p.raw, status);
    const logo = (variant: 'dark' | 'light') => el('img', { src: `${import.meta.env.BASE_URL}geokontroll-logo-${variant}.svg`, alt: 'Geokontroll', class: `header-logo logo-${variant}`, width: 112, height: 32 });
    const projectSelect = el('select',{'aria-label':'Prosjekt',class:'project-select',autocomplete:'off',onchange:(event:Event)=>{const id=(event.target as HTMLSelectElement).value;if(id!==p.projectId){const url=new URL(location.href);url.searchParams.set('project',id);location.href=url.href;}}},el('option',{value:p.projectId},shortProjectName(p.projectName))) as HTMLSelectElement;
    this.loadProjectList(projectSelect);
    const modeSwitch = local ? el('div', { class: 'seg mode-switch', role: 'group', 'aria-label': 'Visning' },
      ...(['result', 'full'] as const).map((m) => { const b = el('button', { type: 'button', class: 'seg-btn', 'aria-pressed': 'false', onclick: () => this.setResultMode(m === 'result') }, m === 'result' ? 'Resultat' : 'Full visning') as HTMLButtonElement; this.modeSeg.set(m, b); return b; })) : null;
    this.themeButton = tip(el('button', { type: 'button', class: 'btn-icon theme-toggle', 'aria-label': 'Bytt lyst/mørkt tema', onclick: () => { applyTheme(toggleTheme(currentTheme())); this.syncThemeButton(); } }, icon('moon')), 'Bytt tema', 'bottom') as HTMLButtonElement;
    const header = el('header', { class: 'topbar' },
      el('div', { class: 'tb-left' },
        el('a', { class: 'brand-link', href: location.pathname + location.search, 'aria-label': 'Geokontroll LiDAR Viewer – last prosjektet på nytt' }, logo('dark'), logo('light')),
        el('span', { class: 'tb-divider', 'aria-hidden': 'true' }),
        el('label', { class: 'project-switch' }, el('span', { class: 'sr-only' }, 'Prosjekt'), projectSelect),
        el('div', { class: 'run-meta', title: [p.projectName, densityText(p.raw)].filter(Boolean).join(' · ') },
          meta.name ? el('span', { class: 'run-name' }, meta.name) : null,
          meta.when ? el('span', { class: 'run-when mono' }, meta.when) : null,
          meta.status ? el('span', { class: `chip chip-${meta.tone ?? 'neutral'}`, title: meta.detail ?? '' }, meta.status) : null,
          this.review.levelChip())),
      el('div', { class: 'tb-center' }, modeSwitch, this.reviewButton,
        el('nav',{'aria-label':'Verktøy',class:'mode-nav'},
          el('button',{type:'button','data-workspace-mode':'analysis',onclick:()=>{this.markWorkspaceMode('analysis');this.toggleSide(true);sQc.setOpen(true);sVectors.setOpen(true);sQc.root.scrollIntoView({block:'nearest'});}},icon('analysis'),'Analyse'),
          el('button',{type:'button','data-workspace-mode':'classify',onclick:()=>this.workbench.show('classify')},icon('classify'),'Klassifisering'),
          el('button',{type:'button','data-workspace-mode':'measure',onclick:()=>this.workbench.show('measure')},icon('measure'),'Målinger'),
          el('button',{type:'button','data-workspace-mode':'crop',onclick:()=>this.workbench.show('crop')},icon('polygon'),'Utsnitt'))),
      el('div', { class: 'tb-right' },
        tip(el('button',{type:'button',class:'btn-icon',  'aria-label':'Hjelp og tastatursnarveier',onclick:()=>this.showHint()},icon('help')), 'Hjelp · ?', 'bottom'),
        this.themeButton,
        tip(el('button',{type:'button',class:'btn-icon object-list-action','aria-label':'Åpne objektliste',onclick:()=>{this.tablePanel.classList.toggle('hidden');this.el.stage?.classList.toggle('table-open',!this.tablePanel.classList.contains('hidden'));this.updateTable();}},icon('list')), 'Objektliste (lastede lag)', 'bottom'),
        tip(el('button',{type:'button',class:'btn-icon hide-ui-action','aria-label':'Skjul paneler',onclick:()=>{this.toggleSide(false);this.closeInfo();this.shell.classList.add('ui-hidden');}},icon('expand')), 'Bare kart (skjul paneler)', 'bottom'),
        this.menuButton,
        el('button',{type:'button',class:'btn btn-primary download-action','data-workspace-mode':'download',onclick:()=>this.workbench.show('download')},icon('download'),el('span',{},'Last ned'))));
    this.backdrop = el('button', { type: 'button', class: 'drawer-backdrop', 'aria-label': 'Lukk paneler', onclick: () => { this.toggleSide(false); this.closeInfo(); } });
    this.shell = el('div', { class: `workspace${mobile ? ' side-collapsed' : ''}${local ? ' result-mode has-result' : ''}` }, header,
      el('div',{class:'layout'},side,center,this.inspector), this.backdrop,el('button',{type:'button',class:'restore-ui btn','aria-label':'Vis paneler',onclick:()=>{this.shell.classList.remove('ui-hidden');this.menuButton.focus();}},icon('layers'),'Vis paneler'));
    this.syncThemeButton();
    this.syncModeSwitch();
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => { if (readThemePref() === 'system') { applyTheme(); this.syncThemeButton(); } });
    this.root.replaceChildren(this.shell);
    panelResizer(side,'left',()=>this.inspector.classList.contains('hidden')?0:this.inspector.getBoundingClientRect().width);
    panelResizer(this.inspector,'right',()=>this.shell.classList.contains('side-collapsed')?0:side.getBoundingClientRect().width);
    this.makeTableResizable();
    const screen = matchMedia('(max-width: 900px)');
    screen.addEventListener('change', () => { this.toggleSide(!screen.matches); this.closeInfo(); });
    this.applyFilter();
    // Tastatur på dokumentnivå: snarveiene skal virke også når ingenting har fokus (f.eks. etter klikk i kartet).
    document.addEventListener('keydown', (e) => {
      if(this.workbench.state.mode==='classify'&&(e.ctrlKey||e.metaKey)&&e.key.toLowerCase()==='z'&&!/INPUT|TEXTAREA|SELECT/.test((e.target as HTMLElement).tagName)){e.preventDefault();if(e.shiftKey)this.workbench.classification.redo();else this.workbench.classification.undo();this.workbench.classification.saveLocal();void this.workbench.classification.saveServer().catch(()=>{});this.workbench.show('classify');return;}
      const action = shortcutFor({ key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, target: e.target as HTMLElement | null });
      if (action === 'escape') {
        if (this.workbench.tool !== 'select') { e.preventDefault(); this.workbench.cancel(); return; }
        if (this.hintEl) { this.closeHint(); return; }
        if (this.shell.classList.contains('ui-hidden')) { this.shell.classList.remove('ui-hidden'); return; }
        if (!this.inspector.classList.contains('hidden')) { this.workbench.close(); this.closeInfo(); return; }
        if (matchMedia('(max-width: 900px)').matches) { this.toggleSide(false); this.menuButton.focus(); }
        return;
      }
      if (action === 'next' || action === 'prev') { if (this.result && this.result.step(action === 'next' ? 1 : -1)) e.preventDefault(); return; }
      if (action === 'focus') { e.preventDefault(); this.focusSelection(); return; }
      if (action === 'help') { e.preventDefault(); this.showHint(); return; }
      if (e.key === 'Tab' && matchMedia('(max-width: 900px)').matches) {
        const panel = !this.shell.classList.contains('side-collapsed') ? side : !this.inspector.classList.contains('hidden') ? this.inspector : null;
        const nodes = panel ? [...panel.querySelectorAll<HTMLElement>('button,input,select,a[href]')].filter((n) => n.getClientRects().length && !(n as HTMLButtonElement).disabled) : [];
        if (nodes.length && (e.shiftKey ? document.activeElement === nodes[0] : document.activeElement === nodes[nodes.length - 1])) {
          e.preventDefault(); (e.shiftKey ? nodes[nodes.length - 1] : nodes[0]).focus();
        }
      }
    });
    let swipeX = 0;
    side.addEventListener('touchstart', (e) => { swipeX = e.touches[0].clientX; }, { passive: true });
    side.addEventListener('touchend', (e) => { if (swipeX - e.changedTouches[0].clientX > 90) this.toggleSide(false); }, { passive: true });

    let down: { x: number; y: number } | null = null;
    const fingers = new Set<number>();
    let multi = false;
    let lastTap = { time: 0, x: 0, y: 0 };
    canvas.addEventListener('pointerdown', (e) => {
      if(this.workbench.tool!=='select')return;
      fingers.add(e.pointerId);
      if (fingers.size > 1) multi = true;
      down = { x: e.clientX, y: e.clientY };
    });
    canvas.addEventListener('pointerup', (e) => {
      if(this.workbench.tool!=='select'){down=null;fingers.clear();return;}
      if (!multi && down && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 6 && e.button === 0) {
        this.pickAt(e.clientX, e.clientY);
        if (e.pointerType === 'touch') {
          const now = performance.now();
          if (now - lastTap.time < 350 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 24) {
            const sel = this.selection;
            if (sel?.type === 'point') this.engine.focusOn(sel.pick.e, sel.pick.n, sel.pick.h);
            if (sel?.type === 'feature') this.engine.focusOn(sel.picked.at[0], sel.picked.at[1], sel.picked.at[2] ?? this.engine.targetAbsolute()?.[2] ?? 0);
          }
          lastTap = { time: now, x: e.clientX, y: e.clientY };
        }
      }
      fingers.delete(e.pointerId);
      if (!fingers.size) multi = false;
      down = null;
    });
    canvas.addEventListener('pointercancel', (e) => { fingers.delete(e.pointerId); down = null; if (!fingers.size) multi = false; });
    canvas.addEventListener('dblclick', (e) => {
      if(this.workbench.tool!=='select')return;
      const v = this.app.overlays.pick(e.clientX, e.clientY);
      if (v) {
        const a = v.at;
        this.engine.focusOn(a[0], a[1], a[2] ?? this.engine.targetAbsolute()?.[2] ?? 0);
        return;
      }
      const r = this.engine.pick(e.clientX, e.clientY);
      if (r) this.engine.focusOn(r.e, r.n, r.h);
    });
    this.setMode(local ? 'classification' : this.engine.mode);
    this.setBasemap(this.app.basemap.available ? cfg.basemap : 'none');
    this.markView();
    this.engine.onChange = () => this.schedule();
    this.app.overlays.onChange = () => this.schedule();
    this.app.basemap.onChange = () => this.schedule();
    this.layerPrefKey = JSON.stringify(visibleLayerIds(this.app.overlays.layers));
    this.schedule();
    if (!hintSeen()) this.showHint();
  }

  private get projectKey(): string { return this.app.project.projectId || this.app.cfg.projectId; }

  /** Husk lagene brukeren har slått på (per prosjekt), slik at en omlasting beholder dem. */
  private persistLayerChoice(): void {
    const ids = visibleLayerIds(this.app.overlays.layers);
    const key = JSON.stringify(ids);
    if (key === this.layerPrefKey) return;
    this.layerPrefKey = key;
    writeVisibleLayers(this.projectKey, ids);
  }

  /** «Tilbakestill visning»: ren standard – alle vektorlag av, alle klasser synlige, hele prosjektet i bildet. */
  resetDefaultView(): void {
    clearVisibleLayers(this.projectKey);
    for (const l of this.app.overlays.layers) if (l.visible) this.app.overlays.setVisible(l.def.id, false);
    this.layerPrefKey = JSON.stringify(visibleLayerIds(this.app.overlays.layers));
    this.hiddenClasses = new Set(); this.classPreset = 'all';
    this.engine.setHiddenClasses(this.hiddenClasses);
    if (this.showUncertainSupports) { this.showUncertainSupports = false; this.applySupportVisibility(); }
    this.select(null);
    this.closeInfo();
    this.result?.reset();
    this.engine.zoomTo(this.engine.projectBounds());
    this.renderLegend();
    this.schedule();
  }

  // ------------------------------------------------------------------ topplinje, hjelp, tema
  private showHint(): void {
    if (this.hintEl) { this.hintEl.querySelector<HTMLButtonElement>('.btn-primary')?.focus(); return; }
    this.hintEl = hintCard({ findings: !!this.result, onClose: () => this.closeHint(), onNext: this.result ? () => { this.setResultMode(true); this.result?.step(1); } : undefined });
    this.el.stage?.append(this.hintEl);
  }

  private closeHint(): void {
    markHintSeen();
    this.hintEl?.remove();
    this.hintEl = null;
  }

  private syncThemeButton(): void {
    const dark = currentTheme() === 'dark';
    this.themeButton.replaceChildren(icon(dark ? 'sun' : 'moon'));
    this.themeButton.dataset.tip = dark ? 'Bytt til lyst tema' : 'Bytt til mørkt tema';
    this.themeButton.setAttribute('aria-label', this.themeButton.dataset.tip);
  }

  private syncModeSwitch(): void {
    const on = this.shell.classList.contains('result-mode');
    for (const [m, b] of this.modeSeg) { const active = (m === 'result') === on; b.classList.toggle('active', active); b.setAttribute('aria-pressed', String(active)); }
    if (this.sideTitle) this.sideTitle.textContent = on ? 'Resultat' : 'Lag og data';
  }

  /** Prosjektvelgeren: fyll inn prosjektene API-et kjenner (lokalt API: /api/test). Feil gir bare gjeldende prosjekt. */
  private loadProjectList(select: HTMLSelectElement): void {
    const { project: p, cfg } = this.app;
    const base = cfg.metadataBase || cfg.apiBase;
    if (p.raw.source !== 'local-api') return;
    void fetch(`${base}/api/test`, { credentials: cfg.apiCredentials }).then((r) => (r.ok ? r.json() : null)).then((j: { projects?: unknown } | null) => {
      const opts = projectOptions({ id: p.projectId, label: shortProjectName(p.projectName) }, j?.projects);
      select.replaceChildren(...opts.map((o) => el('option', { value: o.id, selected: o.id === p.projectId }, o.label)));
    }).catch(() => { /* behold gjeldende prosjekt */ });
  }

  /** F / målknappen: zoom til valgt funn eller objekt, ellers hele prosjektet. */
  private focusSelection(): void {
    const sel = this.selection;
    if (sel?.type === 'feature') { this.goToFeature(sel.picked, this.findingCtx ?? undefined); return; }
    if (sel?.type === 'point') { this.engine.focusOn(sel.pick.e, sel.pick.n, sel.pick.h); return; }
    this.engine.zoomTo(this.engine.projectBounds());
  }

  private seg<T extends string>(label: string, items: { id: T; label: string }[], store: Map<T, HTMLButtonElement>, on: (v: T) => void): HTMLElement {
    return el('div', { class: 'seg-row' }, el('span', { class: 'seg-label' }, label),
      el('div', { class: `seg n${items.length}` }, ...items.map((m) => {
        const b = el('button', { class: 'seg-btn', onclick: () => on(m.id), title: m.label }, m.label);
        store.set(m.id, b);
        return b;
      })));
  }

  private infoContext() {
    const { project: p, cfg } = this.app;
    return {
      horizontalCrs: p.horizontalCrs ?? '', verticalCrs: p.verticalCrs ?? '', apiBase: cfg.metadataBase || cfg.apiBase,
      projectImages:p.raw.images, projectId: p.projectId || cfg.projectId, imageTemplate: cfg.imageUrlTemplate, images: this.app.images,
      focus: (e: number, n: number, h?: number) => this.engine.focusOn(e, n, h ?? this.engine.targetAbsolute()?.[2] ?? 0),
    };
  }

  // ------------------------------------------------------------------ valg/plukking
  pickAt(x: number, y: number): void {
    if (this.app.droneImages?.handleClick(x, y)) return; // kameramarkør → lysboks
    const v = this.app.overlays.pick(x, y);
    if (v) {
      this.select({ type: 'feature', picked: v });
      return;
    }
    const r = this.engine.pick(x, y);
    this.select(r ? { type: 'point', pick: r, layerName: this.engine.layer(r.layer)?.entry.name ?? r.layer } : null);
  }

  /** Resultat ↔ full visning. Ingen funksjoner fjernes; den fulle vieweren er bare skjult i Resultat. */
  setResultMode(on: boolean): void {
    this.shell.classList.toggle('result-mode', on && !!this.result);
    this.syncModeSwitch();
    this.applyLeveranseView(false);
    this.applySupportVisibility();
    if (this.selection) this.select(this.selection, this.findingCtx);
    this.schedule();
  }

  /** Kundevisning (Resultat + «Som kunden ser det»): tegn bare det leveranseprofilen tar med. */
  private get customerView(): boolean { return !!this.result?.customer() && this.shell.classList.contains('result-mode'); }
  private applyLeveranseView(reselect = true): void {
    setLeveranseView(this.customerView, this.profil);
    for (const l of this.app.overlays.layers) if (l.status === 'ready') this.app.overlays.rebuild(l.def.id);
    if (reselect && this.selection) this.select(this.selection, this.findingCtx);
    this.schedule();
  }

  /** Usikre mastkandidater: skjult i både Resultat og Full visning, med mindre «Vis usikre mastkandidater» er på. */
  private applySupportVisibility(): void {
    setHideUnwrittenSupports(!this.showUncertainSupports);
    if (this.uncertainBox) this.uncertainBox.checked = this.showUncertainSupports;
    for (const id of SUPPORT_CANDIDATE_LAYERS) this.app.overlays.rebuild(id);
    this.schedule();
  }

  /** Velg et funn og fly dit (boks rundt objektet, minst 40 m). */
  private goToFeature(picked: Picked, ctx?: FindingContext): void {
    const pts = picked.feature.parts.flat();
    const xs = pts.map((c) => c[0]), ys = pts.map((c) => c[1]), zs = pts.map((c) => c[2]).filter((z) => Number.isFinite(z));
    const z = zs.length ? zs : [this.engine.targetAbsolute()?.[2] ?? 0];
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const r = Math.max(20, (Math.max(...xs) - Math.min(...xs)) / 2 + 10, (Math.max(...ys) - Math.min(...ys)) / 2 + 10);
    this.engine.zoomTo({ min: [cx - r, cy - r, Math.min(...z)], max: [cx + r, cy + r, Math.max(...z)] });
    this.select({ type: 'feature', picked }, ctx ?? null);
  }

  /** «Funn 3 av 61 · Minsteavstand» med forrige/neste øverst i høyrepanelet (bare når valget kom fra stepperen). */
  private renderFindingBar(): void {
    const c = this.findingCtx;
    this.findingBar.classList.toggle('hidden', !c);
    if (!c) { this.findingBar.replaceChildren(); return; }
    this.findingBar.replaceChildren(
      el('div', { class: 'finding-bar-text' }, el('span', { class: 'finding-bar-pos mono' }, `Funn ${(c.index + 1).toLocaleString('nb-NO')} av ${c.total.toLocaleString('nb-NO')}`), el('span', { class: 'finding-bar-kind' }, c.kind)),
      tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label': 'Forrige funn (P)', onclick: () => this.result?.step(-1) }, icon('prev')), 'Forrige · P', 'bottom'),
      tip(el('button', { type: 'button', class: 'btn-icon', 'aria-label': 'Neste funn (N)', onclick: () => this.result?.step(1) }, icon('next')), 'Neste · N', 'left'));
  }

  /** Fallrisiko: bue/del over linja → samme tre sitt kontaktpunkt, så alle delene åpner ett og samme trekort. */
  private canonicalPick(p: Picked): Picked {
    const contacts = this.app.overlays.layer('veg_fall_contacts');
    const i = fallContactIndex(p.layer.def.id, p.feature.props, contacts?.status === 'ready' ? contacts.parsed?.features : undefined);
    if (i < 0 || !contacts?.parsed) return p;
    const feature = contacts.parsed.features[i];
    return { layer: contacts, feature, at: feature.parts[0][0] as [number, number, number?] };
  }

  select(sel: Selection | null, ctx: FindingContext | null = null): void {
    if (sel?.type === 'feature') sel = { type: 'feature', picked: this.canonicalPick(sel.picked) };
    this.workbench.close();this.imageBody.classList.add('hidden');this.infoSection.root.classList.remove('hidden');
    this.selection = sel;
    this.findingCtx = sel?.type === 'feature' ? ctx : null;
    this.renderFindingBar();
    this.inspectorTitle.textContent=this.selectionTitle();
    this.inspectorSub.textContent = this.selectionSub();
    this.app.overlays.select(sel?.type === 'feature' ? sel.picked : null);
    const full = renderSelection(sel, this.infoContext());
    this.infoBody.replaceChildren(this.result && this.shell.classList.contains('result-mode') && sel?.type === 'feature' ? resultCard(sel.picked, full, this.customerView ? this.profil : null) : full);
    if(sel?.type==='feature')this.infoBody.append(this.reviewPanel(sel));
    const droneCard=this.app.droneImages?.selectionActions(sel);if(droneCard)this.infoBody.append(droneCard);
    if (sel) {
      this.inspector.classList.remove('hidden');
      this.shell.classList.add('info-open');
      if (matchMedia('(max-width: 900px)').matches) this.toggleSide(false);
      if (matchMedia('(max-width: 900px)').matches) this.inspector.querySelector<HTMLButtonElement>('button')?.focus();
      this.infoSection.setOpen(true);
      this.inspector.querySelector<HTMLElement>('.inspector-scroll')?.scrollTo({top:0});
    }
    this.onSelect?.(sel);
    this.updateTable();
  }

  private updateTable(): void {
    if (!this.tablePanel || this.tablePanel.classList.contains('hidden')) return;
    const selectedId = this.selection?.type === 'feature' ? `${this.selection.picked.layer.def.id}:${this.selection.picked.feature.id}` : '';
    const key = `${this.tableSearch}:${this.tableSort}:${this.tablePage}:${selectedId}:` + this.app.overlays.layers.map((l) => `${l.def.id}:${l.visible}:${l.status}:${l.parsed?.features.length}:${l.filter}`).join('|');
    if (key === this.tableKey) return;
    this.tableKey = key;
    const rows = filterRows(objectRows(this.app.overlays.layers), this.tableSearch, this.tableSort);
    this.tablePage = Math.min(this.tablePage, Math.max(0, Math.ceil(rows.length / 50) - 1));
    this.tableCount.textContent = `${rows.length} treff · side ${this.tablePage + 1} · kun lastede, synlige lag`;
    const pageButtons = this.tablePanel.querySelectorAll<HTMLButtonElement>('.table-toolbar:last-child button');
    pageButtons[0].disabled = this.tablePage === 0;
    pageButtons[1].disabled = (this.tablePage + 1) * 50 >= rows.length;
    this.tableBody.replaceChildren(...rows.slice(this.tablePage * 50, (this.tablePage + 1) * 50).map((r) => {
      const selected = this.selection?.type === 'feature' && this.selection.picked.layer === r.picked.layer && this.selection.picked.feature === r.picked.feature;
      return el('tr', { class: selected ? 'selected' : '', 'aria-selected': String(selected) },
        el('td', {}, el('button', { class: 'link', onclick: () => this.select({ type: 'feature', picked: r.picked }) }, r.name)),
        el('td', {}, r.layer), el('td', {}, r.status), el('td', {}, this.reviews.get(r.picked.layer.def.id,r.picked.feature.id)?.status ? `Lokal: ${this.reviews.get(r.picked.layer.def.id,r.picked.feature.id)!.status}`:r.review),
        el('td', {}, el('button', { class: 'small-btn', onclick: () => { this.select({ type: 'feature', picked: r.picked }); this.engine.focusOn(r.picked.at[0], r.picked.at[1], r.picked.at[2] ?? this.engine.targetAbsolute()?.[2] ?? 0); } }, 'Zoom')));
    }));
    if (!rows.length) this.tableBody.append(el('tr', {}, el('td', { colSpan: 5 }, this.tableSearch ? 'Ingen treff. Nullstill søket.' : 'Ingen lastede objekter. Slå på et tilgjengelig vektorlag.')));
  }

  private reviewPanel(sel:Extract<Selection,{type:'feature'}>):HTMLElement {
    const {layer,feature}=sel.picked;const record=this.reviews.get(layer.def.id,feature.id);
    const STATUS:Record<string,string>={APPROVED:'Godkjent',REJECTED:'Avvist',CORRECTION:'Korrigering foreslått'};
    const status=el('p',{class:'note','aria-live':'polite'},record?`Lokal review: ${STATUS[record.status]??record.status}`:'Ikke vurdert ennå');
    const note=el('textarea',{'aria-label':'Review / korrigeringsnotat',placeholder:'Beskriv vurdering eller korrigeringsforslag …',value:record?.note??''}) as HTMLTextAreaElement;
    const act=(s:'APPROVED'|'REJECTED'|'CORRECTION')=>{const persisted=this.reviews.set(layer.def.id,feature.id,s,note.value);status.textContent=`Lokal review: ${STATUS[s]}${persisted?'':' · bare i minne; eksporter for å bevare'}`;this.tableKey='';this.updateTable();};
    const next=(direction:number)=>{const rows=objectRows(this.app.overlays.layers);const i=rows.findIndex(r=>r.picked.layer===layer&&r.picked.feature===feature);const item=rows[i+direction];if(item)this.select({type:'feature',picked:item.picked});};
    return el('details',{class:'review-panel disclosure',open:!!record},el('summary',{},'QC / review',el('span',{class:'summary-meta'},record?STATUS[record.status]??record.status:'Ikke vurdert')),
      el('div',{class:'review-body'},status,note,
        el('div',{class:'btn-row',role:'group','aria-label':'Vurdering'},
          el('button',{type:'button',class:'btn btn-sm',onclick:()=>act('APPROVED')},icon('check'),'Godkjenn'),
          el('button',{type:'button',class:'btn btn-sm',onclick:()=>act('REJECTED')},icon('close'),'Avvis'),
          el('button',{type:'button',class:'btn btn-sm',onclick:()=>act('CORRECTION')},icon('classify'),'Korriger')),
        el('div',{class:'btn-row btn-row-ghost'},
          el('button',{type:'button',class:'btn btn-ghost btn-sm',onclick:()=>next(-1)},icon('prev'),'Forrige i objektlisten'),
          el('button',{type:'button',class:'btn btn-ghost btn-sm',onclick:()=>next(1)},'Neste i objektlisten',icon('next'))),
        el('button',{type:'button',class:'btn btn-ghost btn-sm',onclick:()=>saveBlob('review.json',JSON.stringify(this.reviews.export(),null,2),'application/json')},icon('download'),'Eksporter review (JSON)'),
        el('p',{class:'note'},'Lagres bare i denne nettleseren, ikke hos kunden eller i originaldata. «Korriger» er et forslag, ikke ny klassifisering.')));
  }

  private closeInfo(): void {
    if(this.workbench.state.mode)this.workbench.close();
    this.inspector.classList.add('hidden');
    this.shell.classList.remove('info-open');
  }

  private toggleSide(open = this.shell.classList.contains('side-collapsed')): void {
    this.shell.classList.toggle('side-collapsed', !open);
    this.menuButton.setAttribute('aria-expanded', String(open));
    if (open && matchMedia('(max-width: 900px)').matches) this.closeInfo();
    if (open) this.el.side?.querySelector<HTMLButtonElement>('button')?.focus();
  }

  onSelect?: (sel: Selection | null) => void;

  /** Gjengi objektinfo i en annen beholder (mobil bunnark). */
  renderInfoInto(target: HTMLElement): void {
    target.replaceChildren(renderSelection(this.selection, this.infoContext()));
  }

  // ------------------------------------------------------------------ visning
  setView(v: '3d' | '2d'): void {
    this.workbench?.cancel();
    this.engine.setViewMode(v);
    this.markView();
    this.markWorkspaceMode(v);
  }

  private markWorkspaceMode(mode:string):void {
    this.root.querySelectorAll<HTMLElement>('[data-workspace-mode]').forEach(b=>{const active=b.dataset.workspaceMode===mode;b.classList.toggle('active',active);b.setAttribute('aria-pressed',String(active));});
  }

  private markView(): void {
    this.root.querySelectorAll<HTMLElement>('[data-view]').forEach(b=>b.classList.toggle('active',b.dataset.view===this.engine.viewMode));
    for (const [id, b] of this.viewButtons) b.classList.toggle('active', id === this.engine.viewMode);
    this.root.querySelectorAll('.tool-2d3d').forEach((n) => (n.textContent = this.engine.viewMode === '3d' ? '2D' : '3D'));
    const help = this.root.querySelector('.ov-help');
    if (help && help.getAttribute('data-mode') !== this.engine.viewMode) {
      help.setAttribute('data-mode', this.engine.viewMode);
      const item = (k: string, v: string) => el('span', {}, el('b', {}, k), ' ' + v);
      help.replaceChildren(...(this.engine.viewMode === '2d'
        ? [item('Dra', 'panorer'), item('Hjul', 'zoom'), item('Klikk', 'velg')]
        : [item('Venstre', 'roter'), item('Høyre', 'panorer'), item('Hjul', 'zoom'), item('Klikk', 'velg')]));
    }
  }

  resetView(): void {
    this.setView('3d');
    this.engine.zoomTo(this.engine.projectBounds());
  }

  setBasemap(m: BaseMapMode): void {
    if (!this.app.basemap.available) m = 'none';
    this.app.basemap.setMode(m);
    for (const [id, b] of this.mapButtons) {
      b.classList.toggle('active', id === m);
      if (id !== 'none') b.disabled = !this.app.basemap.available;
    }
    this.engine.requestUpdate();
  }

  private layerRow(l: PointcloudLayer): HTMLElement {
    const box = el('input', { type: 'checkbox', 'aria-label':l.entry.name, checked: l.visible, onchange: () => this.engine.setVisible(l.id, box.checked) }) as HTMLInputElement;
    const status = el('span', { class: 'pc-status' });
    const meta = el('span', { class: 'pc-meta' });
    const row = el('div', { class: 'pc-row', title: l.entry.key ?? l.entry.url },
      el('div', { class: 'pc-main' }, box, el('button', { class: 'pc-name layer-name-action', type:'button', 'aria-label':`Detaljer ${l.entry.name}`,onclick:(event:Event)=>{event.preventDefault();this.workbench.close();this.infoSection.root.classList.remove('hidden');this.infoBody.replaceChildren(el('h2',{},l.entry.name),el('div',{class:'kv'},el('span',{},'Filstørrelse'),el('b',{},formatBytes(l.entry.size)),el('span',{},'Punkter'),el('b',{},l.header?formatCount(l.header.pointCount):'Ikke lastet'),el('span',{},'Kilde'),el('b',{},l.entry.key??l.entry.url)),el('button',{class:'small-btn',onclick:()=>this.engine.zoomToLayer(l.id)},'Zoom til punktsky'));this.openInspector('Punktsky');}},l.entry.name)),
      el('span', { class: 'pc-size' }, formatBytes(l.entry.size)),
      el('button', { class: 'icon', title:'Zoom til punktsky','aria-label':`Zoom til ${l.entry.name}`,onclick: () => this.engine.zoomToLayer(l.id) },icon('zoom')),
      el('div', { class: 'pc-sub' }, status, meta));
    this.rows.set(l.id, { row, box, status, meta });
    return row;
  }

  private renderLayerGroups():void {
    const list=this.app.overlays.layers.filter(l=>l.def.inProject&&!hiddenFromLayerList(l.def.id));
    this.vrows.clear();
    this.vectorList.replaceChildren(...list.filter(l=>!['qc','refly','cameras'].includes(l.def.kind)).map(l=>this.vectorRow(l)));
    this.qcList.replaceChildren(...list.filter(l=>['qc','refly'].includes(l.def.kind)).map(l=>this.vectorRow(l)));
    this.imageList.replaceChildren(...list.filter(l=>l.def.kind==='cameras').map(l=>this.vectorRow(l)));
  }
  /** Fallrisiko / minsteavstand: én bryter per tema (flere lag) med kort forklaring. Tom når lagene mangler. */
  private riskGroup(declared: VectorLayer[]): HTMLElement {
    const toggles = [[FALL_TOGGLE, FALL_LEGEND], [CLEARANCE_TOGGLE, CLEARANCE_LEGEND]] as const;
    const rows = toggles.flatMap(([t, legend]) => {
      let layers = declared.filter((l) => t.layers.includes(l.def.id));
      if (!layers.length && 'fallback' in t) layers = declared.filter((l) => t.fallback.includes(l.def.id));
      if (!layers.length) return [];
      const box = el('input', { type: 'checkbox', checked: layers.some((l) => l.visible), 'aria-label': t.title,
        onchange: () => { for (const l of layers) this.app.overlays.setVisible(l.def.id, box.checked); } }) as HTMLInputElement;
      this.riskBoxes.push({ box, layers });
      return [el('label', { class: 'pc-main' }, box, el('span', { class: 'pc-name' }, t.title)), el('div', { class: 'note' }, legend),
        ...(t === FALL_TOGGLE ? [this.fallFilterControl(layers)] : [])];
    });
    if (!rows.length) return el('div', {});
    return el('div', { class: 'risk-group' }, el('div', { class: 'eyebrow' }, 'Vegetasjon mot linje'), ...rows, el('div', { class: 'note' }, VEG_NOTE));
  }
  /** Fallrisiko-filter: én fortegnsverdi s = d3 − H (positiv = mangler s m, negativ = rekker -s m forbi). Huskes per prosjekt. */
  private fallFilterControl(layers: VectorLayer[]): HTMLElement {
    const label = 'Avstand til treff (m)';
    const num = el('input', { type: 'number', id: 'fall-s', min: String(FALL_S_MIN), max: '0', step: '0.5', value: String(fallFilter.s), inputmode: 'decimal' }) as HTMLInputElement;
    const range = el('input', { type: 'range', min: String(FALL_S_MIN), max: '0', step: '0.5', value: String(fallFilter.s), 'aria-label': label }) as HTMLInputElement;
    const count = el('div', { class: 'note', 'aria-live': 'polite' }), empty = el('div', { class: 'note warn', role: 'status' }), note = el('div', { class: 'note warn' }), hint = el('div', { class: 'note' });
    const u = { num, range, count, empty, note, hint };
    const apply = (v: string, commit: boolean) => {
      if (v === '' || v === '-') return;
      setFallFilter({ s: Number(v.replace(',', '.')) }); range.value = String(fallFilter.s); if (commit) num.value = String(fallFilter.s);
      this.applyFallFilter(layers, u, true);
    };
    num.addEventListener('input', () => apply(num.value, false));
    num.addEventListener('change', () => apply(num.value, true));
    range.addEventListener('input', () => apply(range.value, true));
    const box = el('div', { class: 'fall-filter' }, el('div', { class: 'fall-filter-field' }, el('label', { for: 'fall-s' }, label),
      el('div', { class: 'fall-filter-row' }, num, range)), el('div', { class: 'note' }, FALL_HELP), hint, count, empty, note);
    this.fallFilterUpdate = () => this.applyFallFilter(layers, u, false);
    this.fallFilterUpdate();
    return box;
  }
  private fallFilterUpdate?: () => void;
  private applyFallFilter(layers: VectorLayer[], u: Record<'num' | 'range' | 'count' | 'empty' | 'note' | 'hint', HTMLElement>, rebuild: boolean): void {
    const src = layers.find((l) => l.parsed);
    const feats = src?.parsed?.features ?? [];
    const cap = fallMargin(feats), num = u.num as HTMLInputElement, range = u.range as HTMLInputElement;
    // positiv ende begrenses av marginen kjøringen brukte; trær lenger unna finnes ikke i dataene
    if (num.max !== String(cap)) {
      num.max = range.max = String(cap);
      if (fallFilter.s > cap) { setFallFilter({ s: cap }); rebuild = true; }
      num.value = range.value = String(fallFilter.s);
    }
    if (rebuild) {
      writeFallFilter(this.projectKey, fallFilter);
      for (const id of [FALL_LAYER, ...FALL_PART_LAYERS]) this.app.overlays.rebuild(id);
      this.result?.reset(); this.tableKey = ''; this.updateTable();
    }
    const c = src?.parsed ? fallCounts(feats) : null;
    u.count.textContent = c ? fallCountText(c) : '';
    u.empty.textContent = c && c.shown === 0 ? FALL_EMPTY_TEXT : '';
    u.note.textContent = src?.parsed && !c ? FALL_NO_FIELD_NOTE : '';
    u.hint.textContent = c && cap > 0 ? fallMarginHint(cap) : '';
    if (rebuild) this.schedule();
  }
  private vectorRow(l:VectorLayer):HTMLElement {
    const box=el('input',{type:'checkbox',checked:l.visible,'aria-label':`Synlighet ${l.def.title}`,onchange:()=>this.app.overlays.setVisible(l.def.id,box.checked)}) as HTMLInputElement;
    const status=el('span',{class:'pc-status'}),detail=el('div',{class:'layer-reason'});
    const zoom=el('button',{class:'icon',title:`Zoom til ${l.def.title}`,'aria-label':`Zoom til ${l.def.title}`,disabled:true,onclick:()=>{
      const b=l.parsed?.bounds;if(b)this.engine.zoomTo({min:[b[0],b[1],this.engine.targetAbsolute()?.[2]??0],max:[b[2],b[3],this.engine.targetAbsolute()?.[2]??0]});
    }},icon('zoom'));
    let timer:ReturnType<typeof setTimeout>;
    const filter=el('input',{type:'search',class:'vector-filter',placeholder:'ID eller attributt','aria-label':`Filtrer ${l.def.title}`,value:l.filter??'',oninput:(e:Event)=>{clearTimeout(timer);const query=(e.target as HTMLInputElement).value;timer=setTimeout(()=>{this.app.overlays.setFilter(l.def.id,query);this.tableKey='';this.updateTable();},180);}}) as HTMLInputElement;
    const retry=el('button',{class:'small-btn',onclick:()=>void this.app.overlays.load(l)},'Prøv laget igjen');
    const details=el('details',{class:'layer-details'},el('summary',{'aria-label':`Detaljer ${l.def.title}`,title:'Filter, kilde og lastestatus'},'Detaljer'),detail,filter,retry);
    details.addEventListener('toggle',()=>{row.classList.toggle('layer-selected',details.open);});
    const row=el('div',{class:'pc-row v-row','data-layer':l.def.id},
      el('label',{class:'pc-main'},box,el('i',{class:`sw sw-${l.def.kind}`}),el('span',{class:'pc-name'},l.def.title)),zoom,
      el('div',{class:'pc-sub'},status),details);
    this.vrows.set(l.def.id,{row,box,status,detail,filter,retry});return row;
  }
  private selectionSub():string {
    const sel=this.selection;
    return sel?.type==='feature'?`ID ${sel.picked.feature.id}`:sel?.type==='point'?`E ${formatCoord(sel.pick.e,2)} · N ${formatCoord(sel.pick.n,2)}`:'';
  }
  private selectionTitle():string {
    const sel=this.selection;
    if(sel?.type!=='feature')return sel?'LiDAR-punkt':'Objektinfo';
    const def=sel.picked.layer.def,title=def.kind==='other'?def.title:KIND_TITLES[def.kind];
    return cardRows(def.id,title,sel.picked.feature.props).rows[0]?.[1]??title;
  }
  private openInspector(title:string):void {
    this.inspectorTitle.textContent=title;this.inspector.classList.remove('hidden');this.shell.classList.add('info-open');
    if(matchMedia('(max-width: 900px)').matches)this.toggleSide(false);
  }
  private openImages():void {
    this.workbench.close();this.infoSection.root.classList.add('hidden');this.imageBody.classList.remove('hidden');
    const images=projectImages(this.app.project.raw.images,this.app.project.projectId);
    this.imageBody.replaceChildren(gallery(images,this.app.images,this.app.cfg.apiBase,this.app.project.projectId,this.app.cfg.imageUrlTemplate,item=>{
      const camera=this.app.overlays.byKind('cameras').flatMap(layer=>(layer.parsed?.features??[]).map(feature=>({layer,feature}))).find(p=>String(p.feature.props.image_id??p.feature.id)===item.id);
      if(camera){const at=camera.feature.parts[0][0] as [number,number,number?];this.selection={type:'feature',picked:{...camera,at}};this.app.overlays.select(this.selection.picked);this.tableKey='';this.updateTable();}
    }));this.openInspector('Dronebilder · originalfoto');
  }
  private makeTableResizable():void {
    const grip=el('div',{class:'table-resizer',role:'separator','aria-orientation':'horizontal','aria-label':'Høyde på objektliste',tabIndex:0});
    const set=(height:number)=>{const max=(this.el.stage?.clientHeight??600)*.6;const value=Math.max(140,Math.min(max,height));this.tablePanel.style.height=`${value}px`;this.el.stage?.style.setProperty('--table-height',`${value}px`);};
    grip.addEventListener('pointerdown',e=>{e.preventDefault();const start=e.clientY,height=this.tablePanel.clientHeight;grip.setPointerCapture(e.pointerId);
      const move=(ev:PointerEvent)=>set(height+start-ev.clientY);const end=()=>{grip.removeEventListener('pointermove',move);grip.removeEventListener('pointerup',end);grip.removeEventListener('pointercancel',end);};grip.addEventListener('pointermove',move);grip.addEventListener('pointerup',end);grip.addEventListener('pointercancel',end);
    });
    grip.addEventListener('keydown',e=>{if(['ArrowUp','ArrowDown'].includes(e.key)){e.preventDefault();set(this.tablePanel.clientHeight+(e.key==='ArrowUp'?20:-20));}});
    this.tablePanel.prepend(grip);
  }

  private setAll(v: boolean): void {
    for (const l of this.engine.layers) {
      if (this.filter && !this.matches(l)) continue;
      this.engine.setVisible(l.id, v);
      const r = this.rows.get(l.id);
      if (r) r.box.checked = v;
    }
    this.schedule();
  }

  private matches(l: PointcloudLayer): boolean {
    return !this.filter || l.entry.name.toLowerCase().includes(this.filter) || l.id.toLowerCase().includes(this.filter);
  }

  private applyFilter(): void {
    for (const l of this.engine.layers) this.rows.get(l.id)?.row.classList.toggle('hidden', !this.matches(l));
    const count = this.engine.layers.filter((l) => this.matches(l)).length;
    this.filterMessage.textContent = this.filter ? count ? `${count} treff i listen. Søk endrer ikke lagsynlighet.` : 'Ingen treff. Nullstill søket for å se alle filer.' : '';
  }

  private setMode(m: ColorMode): void {
    const hasRgb = this.engine.layers.some((l) => l.header?.hasRgb);
    const anyHeader = this.engine.layers.some((l) => l.header);
    if (m === 'rgb' && anyHeader && !hasRgb) m = 'elevation';
    this.engine.setMode(m);
    for (const [id, b] of this.modeButtons) b.classList.toggle('active', id === m);
    this.legendEl.classList.toggle('hidden', m !== 'classification' && m !== 'elevation' && m !== 'density');
    this.renderLegend();
  }

  private renderLegend(): void {
    const actualHidden=this.engine.style.hiddenClasses;if(actualHidden.size!==this.hiddenClasses.size||[...actualHidden].some(code=>!this.hiddenClasses.has(code))){this.hiddenClasses=new Set(actualHidden);this.classPreset='custom';}
    const m = this.engine.mode;
    const key=JSON.stringify([m,this.classPreset,[...this.hiddenClasses],this.engine.elevationRangeAbsolute(),this.engine.layers.map(l=>[l.id,[...l.classCounts]]),m==='density'?this.engine.densityInfo:null]);
    if(key===this.legendKey)return;this.legendKey=key;
    if (m === 'density') {
      const d = this.engine.densityInfo;
      const f = (v: number) => v.toLocaleString('nb-NO', { maximumFractionDigits: v < 10 ? 1 : 0 });
      const avg = densityText(this.app.project.raw);
      this.legendEl.replaceChildren(el('div', { class: 'eyebrow' }, 'Punkttetthet (pkt/m²)'), el('div', { class: 'ramp', style: `background:${DENSITY_GRADIENT}` }),
        el('div', { class: 'ramp-lbl' }, el('span', {}, '0'), el('span', {}, d ? `${f(d.scaleMax)}+` : '–')),
        el('p', { class: 'muted legend-note' }, d ? d.full
          ? `Alle punkter er lastet. Snitt ${f(d.mean)} pkt/m² i ${d.cell} m-ruter.`
          : `Viser punktene som er lastet nå, ikke hele filen. Snitt ${f(d.mean)} pkt/m² i ${d.cell} m-ruter. Zoom inn for mer detalj.` : 'Ingen punkter lastet ennå.'),
        ...(avg ? [el('p', { class: 'muted legend-note' }, `Hele filen: ${avg.replace(/^Punkttetthet: /, '')}`)] : []));
      return;
    }
    if (m === 'elevation') {
      const r = this.engine.elevationRangeAbsolute();
      this.legendEl.replaceChildren(el('div', { class: 'eyebrow' }, `Høyde (${this.app.project.verticalCrs ?? ''})`), el('div', { class: 'ramp' }),
        el('div', { class: 'ramp-lbl' }, el('span', {}, r ? `${formatCoord(r[0], 0)} m` : '–'), el('span', {}, r ? `${formatCoord(r[1], 0)} m` : '–')));
      return;
    }
    if (m !== 'classification') return;
    const counts = new Map<number, number>();
    for (const l of this.engine.layers) for (const [c, n] of l.classCounts) counts.set(c, (counts.get(c) ?? 0) + n);
    const items = [...counts.entries()].sort((a, b) => a[0] - b[0]);
    this.legendEl.replaceChildren(el('div', { class: 'eyebrow' }, 'LAS-klasser (lastede punkter)'),
      el('select',{'aria-label':'Klassepreset',onchange:(e:Event)=>{const key=(e.target as HTMLSelectElement).value;this.classPreset=key;const codes:Record<string,number[]>={ground:[2],vegetation:[3,4,5],wire:[13,14],poles:[15,16,64,65],problem:[7,18]};this.hiddenClasses=new Set(key==='all'?[]:Array.from({length:256},(_,i)=>i).filter(i=>!codes[key]?.includes(i)));this.engine.setHiddenClasses(this.hiddenClasses);this.renderLegend();}},...['all','ground','vegetation','wire','poles','problem','custom'].map((k,i)=>el('option',{value:k,selected:k===this.classPreset,disabled:k==='custom'},['Alle','Bare bakke','Bare vegetasjon','Bare ledninger','Bare mast/bardun/travers','Støy/problemklasser','Tilpasset'][i]))),
      ...(items.length ? items.map(([c, n]) => {
        const info = classInfo(c);
        const cb = el('input', { type: 'checkbox', checked: !this.hiddenClasses.has(c), onchange: () => {
          this.classPreset='custom';
          if (cb.checked) this.hiddenClasses.delete(c);
          else this.hiddenClasses.add(c);
          this.engine.setHiddenClasses(this.hiddenClasses);this.renderLegend();
        } }) as HTMLInputElement;
        return el('label', { class: 'lg-row' }, cb, el('i', { style: `background:${info.color}` }), el('span', {}, `${c} · ${info.name}`), el('small', {}, formatCount(n)));
      }) : [el('div', { class: 'muted' }, 'Ingen punkter lastet ennå')]));
  }

  /** Panel-/statusoppdatering: samles til én per rammevindu og maks ~7 per sekund (strømming kaller dette per node). */
  schedule(): void {
    if (this.raf) return;
    const wait = Math.max(0, 150 - (performance.now() - this.lastRefresh));
    const run = () => { this.raf = 0; this.lastRefresh = performance.now(); this.refresh(); };
    this.raf = wait > 0 ? window.setTimeout(() => requestAnimationFrame(run), wait) : requestAnimationFrame(run);
  }
  private lastRefresh = 0;

  private refresh(): void {
    this.workbench?.update();this.markView();
    this.review?.syncClassEdits();
    this.result?.update();
    this.updateTable();
    let ready = 0;
    let errors = 0;
    for (const l of this.engine.layers) {
      if(!this.rows.has(l.id))this.cloudList?.append(this.layerRow(l));
      const r = this.rows.get(l.id);
      if (!r) continue;
      if (l.status === 'ready') ready++;
      if (l.status === 'error') errors++;
      r.row.classList.toggle('is-error', l.status === 'error');
      r.row.classList.toggle('is-off', !l.visible);
      r.box.checked=l.visible;
      const busy = l.loading.size > 0;
      let text: string;
      let cls: string;
      if (l.status === 'error') { text = `Feil: ${l.error ?? 'ukjent'}`; cls = 'error'; }
      else if (l.status === 'opening') { text = 'Åpner …'; cls = 'busy'; }
      else if (l.status === 'pending') { text = l.visible ? 'Venter' : 'Ikke lastet'; cls = ''; }
      else if (busy) { text = `Laster ${l.loading.size} node${l.loading.size > 1 ? 'r' : ''} …`; cls = 'busy'; }
      else { text = l.visible ? 'Klar' : 'Skjult'; cls = l.visible ? 'ok' : ''; }
      if (l.failedNodes) text += ` · ${l.failedNodes} node${l.failedNodes > 1 ? 'r' : ''} feilet`;
      r.status.className = `pc-status ${cls}`;
      r.status.textContent = text;
      const h = l.header;
      r.meta.textContent = h ? `${formatCount(l.pointsShown)} / ${formatCount(h.pointCount)} pkt` : '';
      if (l.status === 'error' && !r.status.querySelector('button')) {
        r.status.append(' ', el('button', { class: 'link', onclick: () => this.engine.retryLayer(l.id) }, 'Prøv igjen'));
      }
    }
    this.countEl.textContent = `${ready}/${this.engine.layers.length} klare${errors ? ` · ${errors} feil` : ''}`;
    for (const { box, layers } of this.riskBoxes) box.checked = layers.some((l) => l.visible);
    this.fallFilterUpdate?.();
    this.persistLayerChoice();
    for (const l of this.app.overlays.layers) {
      const r = this.vrows.get(l.def.id);
      if (!r) continue;
      r.box.checked = l.visible;
      const n=l.parsed?.features.length??0,failed=['error','unavailable','network_error','access_error'].includes(l.status);
      const drawn=l.visible&&l.status==='ready'&&l.group.children.length>0;
      r.status.textContent=l.status==='ready'?`${drawn?'Vist':'Lastet, ikke vist'} · ${n.toLocaleString('nb-NO')} objekter`:VSTATUS[l.status];
      r.detail.replaceChildren(el('div',{},l.message??(l.status==='empty'?'Kilden er lest, men har ingen objekter.':l.status==='idle'?'Slå på laget for å laste kilden.':'')),
        el('div',{class:'note'},`Ønsket synlighet: ${l.visible?'på':'av'} · faktisk tegnet: ${drawn?'ja':'nei'}`),el('code',{},l.def.url||'Lokal import'));
      r.filter.classList.toggle('hidden',l.status!=='ready');r.retry.classList.toggle('hidden',!failed||!l.def.url);
      const zoom=r.row.querySelector<HTMLButtonElement>('button.icon');if(zoom)zoom.disabled=l.status!=='ready'||!l.parsed?.bounds;
      r.status.className=`pc-status ${l.status==='ready'?'ok':l.status==='loading'?'busy':failed?'error':''}`;
      r.row.classList.toggle('is-off',!l.visible);
    }
    const missing=this.app.overlays.layers.filter(l=>l.def.inProject&&['unavailable','error','network_error','access_error'].includes(l.status));
    this.layerSummary.textContent=missing.length?`${missing.length} lag har kilde-/tilgangsfeil. Årsak og retry finnes under lagets Detaljer.`:'';
    this.layerSummary.classList.toggle('hidden',!missing.length);
    const cams=this.app.overlays.byKind('cameras').filter(l=>l.status==='ready'),photos=projectImages(this.app.project.raw.images,this.app.project.projectId);
    this.cameraInfo.textContent=cams.length?`${cams.reduce((n,l)=>n+(l.parsed?.features.length??0),0)} kameraposisjoner · originalfoto lastes ved klikk.`:photos.length?`${photos.length} originalfoto. Posisjon vises bare når kameradata finnes.`:'Ingen dronebilder eller kameraposisjoner tilgjengelig i prosjektet. Ortofoto/kart er en annen datakilde.';
    const s = this.engine.stats;
    this.statsEl.replaceChildren(
      el('span', { class: 'mono', title: 'Punkter vist / punktbudsjett' }, el('b', {}, formatCount(s.pointsShown)), ` / ${formatCount(s.pointBudget)} pkt`),
      el('span', { class: `st ${ready + errors < this.engine.layers.length || s.requestsInFlight ? 'busy' : errors ? 'err' : 'ok'}` }, ready + errors < this.engine.layers.length ? `Åpner ${ready}/${this.engine.layers.length} filer` : s.requestsInFlight ? `Laster (${s.requestsInFlight})` : errors ? `${errors} filfeil` : 'Klar'),
      ...(s.budgetReached ? [el('span', { class: 'st warn', title: 'Punktbudsjettet er brukt opp. Zoom inn for flere detaljer, eller øk budsjettet under Punktskyvisning (Full visning).' }, 'Budsjett nådd')] : []));
    const t = this.engine.targetAbsolute();
    this.coordEl.textContent = t ? `E ${formatCoord(t[0], 1)}  N ${formatCoord(t[1], 1)}  H ${formatCoord(t[2], 1)} m` : '';
    const bm = this.app.basemap.status();
    if(this.pendingNationalTarget&&this.engine.origin){
      const [x,y]=this.pendingNationalTarget,origin=this.engine.origin,target=this.engine.targetAbsolute();
      if(target&&Math.hypot(target[0]-x,target[1]-y)>20)this.pendingNationalTarget=null;
      else {
        const z=this.app.basemap.terrain.trySample(x-origin[0],y-origin[1],50);
        if(z!==undefined){this.pendingNationalTarget=null;this.engine.focusOn(x,y,z+origin[2]);}
      }
    }
    if(bm.loading||bm.queued)this.statsEl.append(el('span',{class:'st busy'},`Kart/terreng: ${bm.loading} laster, ${bm.queued} venter`));
    if(bm.errors)this.statsEl.append(el('span',{class:'st warn'},`Kart/terreng: ${bm.errors} kildefeil · kun tilgjengelige høyder vises · kontrollert retry`));
    if(bm.noDataTiles)this.statsEl.append(el('span',{class:'st warn'},'Terreng har manglende høyder i utsnittet'));
    this.attrEl.replaceChildren(el('a', { href: 'https://www.kartverket.no/api-og-data/vilkar-for-bruk', target: '_blank', rel: 'noopener noreferrer' }, bm.attribution));
    this.attrEl.classList.toggle('hidden', !bm.attribution);
    this.updateCrsCheck();
    if(this.techVisible)this.updateTech();
    if (this.engine.mode === 'classification' || this.engine.mode === 'elevation' || this.engine.mode === 'density') this.renderLegend();
    const anyHeader = this.engine.layers.some((l) => l.header);
    const rgbBtn = this.modeButtons.get('rgb');
    if (rgbBtn && anyHeader) {
      const hasRgb = this.engine.layers.some((l) => l.header?.hasRgb);
      rgbBtn.disabled = !hasRgb;
      rgbBtn.title = hasRgb ? 'RGB' : 'Ingen av filene har RGB (punktformat uten farge)';
    }
  }

  private updateTech(): void {
    const o = this.engine.origin;
    const s = this.engine.stats;
    const bm = this.app.basemap.status();
    const rows: [string, string][] = [
      ['API', this.app.cfg.apiBase],
      ['Lokal origin', o ? `E ${formatCoord(o[0], 0)} · N ${formatCoord(o[1], 0)} · H ${formatCoord(o[2], 0)}` : '–'],
      ['Visning', `${this.engine.viewMode.toUpperCase()} · ${formatCoord(this.engine.metersPerPixel(), 2)} m/px`],
      ['Nedlastet', `${formatBytes(s.bytesDownloaded)} · ${s.nodesLoaded} noder`],
      ['Bakgrunn', bm.available ? `${bm.mode}${bm.zoom !== null && bm.mode !== 'none' ? ` · nivå ${bm.zoom} · ${bm.tiles} fliser${bm.loading ? ` (${bm.loading} laster)` : ''}${bm.errors ? ` · ${bm.errors} feil` : ''}` : ''}` : (bm.reason ?? 'ikke tilgjengelig')],
      ['Nasjonalt terreng', bm.terrain ? `${bm.heightTiles} høydefliser · ${bm.noDataTiles} med hull · EPSG:25833 → prosjekt · NN2000 iht. kildedokumentasjon` : 'Av'],
      ['Kart/terreng nettverk', `${bm.requests} forespørsler · ${formatBytes(bm.bytes)} raster/bilder · ${bm.loading} aktive · ${bm.queued} i kø · maks 4 bilder + 2 høyder`],
      ['Kart-cache / CPU', `${bm.cacheTiles} fliser · siste oppdatering ${bm.updateMs.toFixed(1)} ms`],
      ['Render CPU (ikke GPU)', `${this.engine.renderMetrics.lastCpuMs.toFixed(1)} ms · maks ${this.engine.renderMetrics.maxCpuMs.toFixed(1)} ms · ${this.engine.renderer.info.render.calls} draw calls`],
      ['FPS under bevegelse', this.engine.renderMetrics.movingSamples ? `${this.engine.renderMetrics.movingFps.toFixed(0)} FPS · p95 ${this.engine.renderMetrics.movingP95Ms.toFixed(1)} ms · ${this.engine.renderMetrics.movingSamples} siste rAF-prøver` : 'Ikke målt ennå'],
      ['GPU-ressurser (antall)', `${this.engine.renderer.info.memory.textures} teksturer · ${this.engine.renderer.info.memory.geometries} geometrier`],
      ['COPC Range', `${this.engine.rangeMetrics.attempts} forsøk / ${this.engine.rangeMetrics.requests} svar / ${this.engine.rangeMetrics.cancelled} avbrutte nodejobber · ${formatBytes(this.engine.rangeMetrics.bytes)} · ${this.engine.rangeMetrics.duplicateRanges} gjentatte områder inkl. avbrudd/retry (siste 4096/worker)`],
      ['Oppstart / hovedtråd', `${Math.round(this.engine.audit.snapshot().first_visible_cloud_ms??0)} ms til første punkter · ${this.engine.audit.longTasks} lange oppgaver (>50 ms)`],
    ];
    this.techEl.replaceChildren(el('div', { class: 'kv tech-kv' }, ...rows.flatMap(([k, v]) => [el('span', {}, k), el('code', {}, v)])),
      el('button',{onclick:()=>{const report=JSON.stringify({schema:'viewer-performance-1',created_at:new Date().toISOString(),project:this.app.project.projectId,viewport:{width:this.engine.renderer.domElement.clientWidth,height:this.engine.renderer.domElement.clientHeight,pixel_ratio:this.engine.renderer.getPixelRatio()},audit:this.engine.audit.snapshot(),render:this.engine.renderMetrics,copc:this.engine.stats,range:this.engine.rangeMetrics,basemap:this.app.basemap.status(),resources:this.engine.renderer.info.memory,origin:this.engine.origin,camera:{position:this.engine.camera.position.toArray(),target:this.engine.controls.target.toArray(),zoom:this.engine.camera.zoom},sources:this.engine.layers.filter(l=>l.header).map(l=>({id:l.id,point_count:l.header!.pointCount,min:l.header!.min,max:l.header!.max,scale:l.header!.scale,offset:l.header!.offset,epsg:l.header!.epsg,samples:[...l.loaded].filter(([,n])=>n.mesh.visible).map(([key,node])=>({key,points:node.count,first_xyz:Array.from(node.mesh.geometry.getAttribute('position').array.slice(0,3)).map((v,i)=>v+this.engine.origin![i])}))})),point_buffer_bytes:this.engine.layers.reduce((n,l)=>n+[...l.loaded.values()].reduce((s,node)=>s+node.bytes,0),0)},null,2);const output=el('pre',{'aria-label':'Ytelsesmåling JSON'},report);this.techEl.append(output);saveBlob('viewer-performance.json',report,'application/json');}},'Eksporter ytelsesmåling'),
      el('div', { class: 'note' }, 'Visningen bruker lokal origin (koordinat − origin) for presisjon i WebGL. Alle viste koordinater er i prosjektets CRS. Kart og vektorlag føres inn i samme ramme; punktskyen transformeres aldri.'));
  }

  private updateCrsCheck(): void {
    const hs = this.engine.layers.filter((l) => l.header);
    const key=JSON.stringify([this.app.project.horizontalCrs,this.app.project.verticalCrs,hs.map(l=>[l.id,l.header!.epsg,l.header!.min,l.header!.max])]);
    if(key===this.crsKey)return;this.crsKey=key;
    if (!hs.length) return;
    const assessment=crsAssessment(this.app.project.horizontalCrs,this.app.project.verticalCrs,hs.map(l=>l.header!));
    this.crsCheckEl.textContent=assessment.text;
    this.crsCheckEl.classList.toggle('warn',assessment.warning);
  }
}

/** Tooltip med tekst (CSS: [data-tip]). Erstatter native title så det ikke blir to tooltips. */
function tip<T extends HTMLElement>(b: T, text: string, pos: 'top' | 'bottom' | 'left' | 'right' = 'bottom'): T {
  b.dataset.tip = text;
  b.dataset.tipPos = pos;
  b.removeAttribute('title');
  return b;
}

const logos = () => ['dark', 'light'].map((v) => el('img', { class: `header-logo logo-${v}`, src: `${import.meta.env.BASE_URL}geokontroll-logo-${v}.svg`, alt: 'Geokontroll', width: 140, height: 40 }));

export function renderFatal(root: HTMLElement, title: string, detail: string, url: string, retry: () => void): void {
  root.replaceChildren(el('div', { class: 'splash' },
    el('div', { class: 'splash-card', role: 'alert' },
      el('div', { class: 'splash-logo' }, ...logos()),
      el('h1', {}, title),
      el('p', {}, detail),
      el('p', { class: 'note' }, 'Kontroller at API-et kjører og at prosjekt-ID-en i adressen er riktig. Adressen som ble forsøkt:'),
      el('code', {}, url),
      el('button', { type: 'button', class: 'btn btn-primary', onclick: retry }, 'Prøv igjen'))));
}

export function renderLoading(root: HTMLElement, text: string): void {
  root.replaceChildren(el('div', { class: 'splash', 'aria-busy': 'true' },
    el('div', { class: 'splash-card' },
      el('div', { class: 'splash-logo' }, ...logos()),
      el('div', { class: 'progress', role: 'progressbar', 'aria-label': text }, el('span')),
      el('p', { 'aria-live': 'polite' }, text))));
}
